import React from 'react'
import { FormattedMessage } from 'react-intl'
import { useWindowScroll } from 'react-use'
import { makeStyles } from 'tss-react/mui'
import ButtonBase from '@mui/material/ButtonBase'
import Fade from '@mui/material/Fade'
import { KeyboardArrowUp } from '@mui/icons-material'
import messages from '@utils/messages'
import windowScrollTo from '@utils/windowScrollTo'
import { sendCommonDetailClickEvent } from '@src/utils/analytics'
import {
  regexForStripHTML,
  regexForStripSymbols,
} from '@src/utils/analytics/coreAnalytics'
import {
  EventActionType,
  EventType,
  EventActionComponentType,
  EventActionElementType,
} from '@src/utils/analytics/enums'

const useStyles = makeStyles()((theme) => ({
  root: {
    position: 'fixed',
    right: theme.spacing(6),
    bottom: theme.spacing(31.5),
    backgroundColor: theme.palette.primary.main,
    height: 56,
    width: 56,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textTransform: 'uppercase',
    boxShadow: '0 2px 4px rgba(0,0,0,.2)',
    zIndex: 2,
  },
  icon: {
    fontSize: 28,
    marginTop: -5,
  },
  text: { fontSize: 14, marginTop: -6 },
}))

const handleScrollToTop = ({
  productNumber,
  productBrand,
  productName,
  productVariant,
}) => {
  sendCommonDetailClickEvent({
    event: EventType.Navigation_Interaction,
    action: EventActionType.InPageClick,
    detail: 'jump to top',
    component: EventActionComponentType.Body,
    elementType: EventActionElementType.AnchorButton,
    elementText: 'top',
    coreEvent: 'no',
    productBrand,
    productId: productNumber,
    productName,
    productVariant,
  })

  windowScrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth',
  })
}

interface BackToTopButtonInput {
  productBrand?: string
  productName?: string
  productNumber?: string
  productVariant?: string
}

const BackToTopButton: React.FC<BackToTopButtonInput> = ({
  productNumber,
  productBrand,
  productName,
  productVariant,
}) => {
  const { y } = useWindowScroll()
  const visible = y > 1000
  const { classes } = useStyles()

  return (
    <Fade in={visible}>
      <ButtonBase
        className={classes.root}
        data-testid="back-to-top-button"
        onClick={() =>
          handleScrollToTop({
            productNumber,
            productBrand,
            productName: productName
              ?.replace(regexForStripHTML, '')
              .replace(regexForStripSymbols, '')
              .toLowerCase(),
            productVariant,
          })
        }
      >
        <div>
          <KeyboardArrowUp className={classes.icon} />
        </div>
        <FormattedMessage {...messages.TOP}>
          {(text) => <div className={classes.text}>{text}</div>}
        </FormattedMessage>
      </ButtonBase>
    </Fade>
  )
}

export default BackToTopButton
